/**
 * Cook & batch: start from the RAW food (its nutrient profile is the truth),
 * say how it was cooked, and let the weight change do the rest. Works with a
 * scale (most accurate), a package label (e.g. a 1 lb pack) or eyeballing.
 */
import { useMemo, useState, type ReactElement } from 'react';
import {
  AMOUNT_METHODS, classifyFood, cook, COOKING_FATS, defaultFatHandling, FAT_HANDLING_LABEL, MAX_CALORIE_REDUCTION, METHOD_LABEL, methodsFor,
  portionUncertainty, rawFromCooked, usesFatHandling, type AddedFat, type AmountMethod, type CookingFatId, type CookMethod, type CookResult, type FatHandling, type FoodKind,
} from '@engine/cooking';
import { AddToDiaryModal } from '../components/AddToDiaryModal';
import { unitsFor, type AmountUnit } from '../components/FoodDetail';
import { FoodSearch } from '../components/FoodSearch';
import { Icon } from '../components/Icon';
import { NutrientPanel } from '../components/NutrientPanel';
import { Card, Empty, Segmented, Spinner } from '../components/ui';
import { effectiveNutrients, itemFromFood } from '../lib/foodNutrients';
import { errorMessage } from '../lib/logger';
import type { Food, FoodWithFill, MealItem, NutrientMap, NutrientSourceMap } from '../lib/types';
import { useApp } from '../state/app';

type Mode = 'batch' | 'portion';
type EatBy = 'grams' | 'pieces' | 'fraction';
type FatUnit = 'tbsp' | 'tsp' | 'g';

const COOKED_WORDS: RegExp = /\b(cooked|roasted|grilled|fried|boiled|baked|broiled|braised|stewed|steamed|pan-browned|pan-broiled|microwaved)\b/i;
const MASS_UNITS: ReadonlyArray<{ id: string; label: string; grams: number }> = [
  { id: 'g', label: 'g', grams: 1 }, { id: 'oz', label: 'oz', grams: 28.3495 }, { id: 'lb', label: 'lb', grams: 453.592 },
];
const FRACTIONS: ReadonlyArray<{ label: string; value: number }> = [
  { label: 'All of it', value: 1 }, { label: '½', value: 1 / 2 }, { label: '⅓', value: 1 / 3 }, { label: '¼', value: 1 / 4 }, { label: '⅕', value: 1 / 5 }, { label: '⅙', value: 1 / 6 }, { label: '⅛', value: 1 / 8 },
];

const fmt = (n: number, d: number = 0): string => n.toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });

function shortName(name: string): string {
  return name.split(',').slice(0, 3).join(',').trim();
}

export function CookPage(): ReactElement {
  const { source, toast } = useApp();
  const [picked, setPicked] = useState<FoodWithFill | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('batch');
  const [qty, setQty] = useState<string>('1');
  const [unitId, setUnitId] = useState<string>('lb');
  const [amountMethod, setAmountMethod] = useState<AmountMethod>('package');
  const [method, setMethod] = useState<CookMethod | ''>('');
  const [handling, setHandling] = useState<FatHandling>('drained');
  const [cookedQty, setCookedQty] = useState<string>('');
  const [cookedUnit, setCookedUnit] = useState<string>('g');
  const [pieces, setPieces] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [savedFood, setSavedFood] = useState<Food | null>(null);
  const [eatBy, setEatBy] = useState<EatBy>('fraction');
  const [eatQty, setEatQty] = useState<string>('1');
  const [eatFraction, setEatFraction] = useState<number>(1 / 4);
  const [eatMethod, setEatMethod] = useState<AmountMethod>('eyeball');
  const [logging, setLogging] = useState<MealItem | null>(null);
  const [fatId, setFatId] = useState<CookingFatId | ''>('');
  const [fatUsed, setFatUsed] = useState<string>('');
  const [fatLeft, setFatLeft] = useState<string>('');
  const [fatUnit, setFatUnit] = useState<FatUnit>('tbsp');

  const pick = async (id: number): Promise<void> => {
    setLoadError(null); setSavedFood(null);
    try {
      const d: FoodWithFill = await source.getFood(id);
      setPicked(d);
      const kind: FoodKind = classifyFood(d.food.name, d.food.category);
      const m: CookMethod | undefined = methodsFor(kind)[0];
      setMethod(m ?? '');
      if (m) setHandling(defaultFatHandling(m));
      setName(`${shortName(d.food.name).replace(/,?\s*raw\b/i, '')}, cooked`);
      if (kind === 'grain' || kind === 'legume') { setQty('1'); setUnitId('p0'); setAmountMethod('eyeball'); }
    } catch (err) {
      setLoadError(errorMessage(err));
    }
  };

  const food: Food | null = picked?.food ?? null;
  const eff = useMemo(() => (picked ? effectiveNutrients(picked.food, picked.fill) : null), [picked]);
  const kind: FoodKind = food ? classifyFood(food.name, food.category) : 'other';
  const methods: CookMethod[] = methodsFor(kind);
  const units: AmountUnit[] = useMemo(() => unitsFor(food?.portions ?? []), [food]);
  const allUnits: AmountUnit[] = useMemo(() => [...MASS_UNITS.filter((m) => !units.some((u) => u.id === m.id)).map((m) => ({ ...m })), ...units], [units]);
  const unit: AmountUnit = allUnits.find((u) => u.id === unitId) ?? allUnits[0]!;
  const amountGrams: number = Math.max(0, Number(qty) || 0) * unit.grams;
  const cookedG: number | null = cookedQty.trim() === '' ? null : Math.max(0, Number(cookedQty) || 0) * (MASS_UNITS.find((u) => u.id === cookedUnit)?.grams ?? 1);
  const nPieces: number = Math.max(0, Math.floor(Number(pieces) || 0));
  // Oil or butter that stays in the food: used minus what was left in the pan.
  const addedFat: AddedFat | null = useMemo(() => {
    if (fatId === '' || mode !== 'batch') return null;
    const perUnit: number = fatUnit === 'g' ? 1 : fatUnit === 'tbsp' ? COOKING_FATS[fatId].tbspG : COOKING_FATS[fatId].tbspG / 3;
    const grams: number = Math.max(0, (Math.max(0, Number(fatUsed) || 0) - Math.max(0, Number(fatLeft) || 0)) * perUnit);
    return grams > 0 ? { fat: fatId, grams } : null;
  }, [fatId, fatUsed, fatLeft, fatUnit, mode]);

  // In "portion" mode the amount entered is COOKED; convert it back to raw using the yield.
  const result: CookResult | { error: string } | null = useMemo(() => {
    if (!food || !eff || amountGrams <= 0) return null;
    const m: CookMethod | null = method === '' ? null : method;
    if (mode === 'batch') {
      return cook({ food: { name: food.name, category: food.category }, per100Raw: eff.values, rawGrams: amountGrams, method: m, handling, cookedGrams: cookedG, addedFat });
    }
    const probe = cook({ food: { name: food.name, category: food.category }, per100Raw: eff.values, rawGrams: 100, method: m, handling, cookedGrams: null });
    if ('error' in probe) return probe;
    const raw = rawFromCooked(amountGrams, probe.yield);
    return cook({ food: { name: food.name, category: food.category }, per100Raw: eff.values, rawGrams: raw.grams, method: m, handling, cookedGrams: null });
  }, [food, eff, amountGrams, method, handling, cookedG, mode, addedFat]);

  const r: CookResult | null = result && !('error' in result) ? result : null;
  // How the amount was measured + how the yield was obtained (a weighed cooked batch has no yield error).
  const uncertainty: number = r ? portionUncertainty(amountMethod, r.yield) : 0;

  const eatenGrams: number = !r ? 0
    : eatBy === 'grams' ? Math.max(0, Number(eatQty) || 0)
    : eatBy === 'pieces' && nPieces > 0 ? (r.cookedGrams / nPieces) * Math.max(0, Number(eatQty) || 0)
    : r.cookedGrams * eatFraction;

  const eatenUncertainty: number = eatBy === 'grams'
    ? Math.sqrt(uncertainty ** 2 + (AMOUNT_METHODS.find((m) => m.id === eatMethod)?.relError ?? 0.3) ** 2)
    : uncertainty;

  const saveBatch = async (): Promise<void> => {
    if (!r || !food) return;
    setSaving(true);
    try {
      const sources: NutrientSourceMap = Object.fromEntries(Object.keys(r.per100Cooked).map((k) => [k, 'calc' as const]));
      const portions = [
        { label: `Whole batch (${fmt(r.cookedGrams)} g cooked)`, grams: r.cookedGrams },
        ...(nPieces > 1 ? [{ label: `1 of ${nPieces} pieces`, grams: Math.round((r.cookedGrams / nPieces) * 10) / 10 }] : []),
        { label: '½ batch', grams: Math.round((r.cookedGrams / 2) * 10) / 10 },
        { label: '¼ batch', grams: Math.round((r.cookedGrams / 4) * 10) / 10 },
      ];
      const f: Food = await source.createFood({
        source: 'recipe',
        name: (name.trim() || `${shortName(food.name)}, cooked`).slice(0, 300),
        brand: null,
        category: food.category,
        nutrients: r.per100Cooked as NutrientMap,
        nutrient_sources: sources,
        portions,
        notes: [
          `From ${fmt(r.rawGrams)} g raw “${food.name}” (FDC ${food.fdc_id ?? 'n/a'}).`,
          method ? `${METHOD_LABEL[method]}; ${usesFatHandling(kind) ? FAT_HANDLING_LABEL[handling].toLowerCase() : 'no fat change'}.` : 'Cooked; weighed before and after.',
          `Yield ${fmt(r.yield.yield * 100, 1)}%: ${r.yield.basis}`,
          r.fatRemovedG > 0 ? `Fat removed: ${fmt(r.fatRemovedG, 1)} g (${fmt(r.kcalRemovedShare * 100)}% of calories${r.capped ? ', capped at 20%' : ''}).` : '',
          r.fatAddedG > 0 && addedFat ? `Cooked with ${fmt(r.fatAddedG, 1)} g of ${COOKING_FATS[addedFat.fat].name.toLowerCase()} (+${fmt(r.kcalAdded)} kcal).` : '',
        ].filter(Boolean).join(' ').slice(0, 2000),
        ai_model: null,
      });
      setSavedFood(f);
      toast(`Saved “${f.name}” to My foods`);
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setSaving(false);
    }
  };

  const logPortion = (): void => {
    if (!r || !food || eatenGrams <= 0) return;
    const base: Food = savedFood ?? { ...food, id: food.id, name: name.trim() || `${shortName(food.name)}, cooked`, nutrients: r.per100Cooked };
    const label: string = eatBy === 'pieces' ? `piece${Number(eatQty) === 1 ? '' : 's'}` : eatBy === 'fraction' ? 'of batch' : 'g';
    const qtyOut: number = eatBy === 'grams' ? Math.round(eatenGrams) : eatBy === 'pieces' ? Number(eatQty) || 0 : Math.round(eatFraction * 1000) / 1000;
    const item: MealItem = itemFromFood(base, r.per100Cooked, eatenGrams, qtyOut, label);
    if (!savedFood) delete item.food_id; // not linked to a saved food: the raw food isn't what was eaten
    setLogging(item);
  };

  const warnCooked: boolean = !!food && COOKED_WORDS.test(food.name) && !/\braw\b/i.test(food.name);

  return (
    <div className="stack">
      <div className="note info" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="info" size={18} />
        <span>Start from the <b>raw</b> food. Cooking mostly changes the <b>weight</b> (water cooks out of meat; rice and pasta soak it up), so the calories <b>per gram</b> change with the method while the whole batch stays about the same. Two things change the batch’s calories: fat that drips or drains away (capped at {Math.round(MAX_CALORIE_REDUCTION * 100)}%), and oil or butter you cook with, which you can add below.</span>
      </div>

      <div className="grid cols-main">
        <div className="stack">
          <Card title="1. Raw ingredient">
            {food ? (
              <div className="stack" style={{ gap: 10 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong style={{ fontSize: 15.5 }}>{food.name}</strong>
                  <button type="button" className="btn btn-sm" onClick={() => { setPicked(null); setSavedFood(null); }}>Change</button>
                </div>
                {warnCooked && <div className="note warn">This looks like an already-cooked food. For the best numbers, pick the raw version (e.g. “…, raw”).</div>}
                {kind === 'other' && <div className="note info">There’s no published cooking yield for this kind of food, so weigh it before and after cooking.</div>}
              </div>
            ) : (
              <FoodSearch autoFocus onPick={(h) => void pick(h.id)} placeholder='Search the RAW food, e.g. "ground beef 80% raw"' />
            )}
            {loadError !== null && <div className="note err" style={{ marginTop: 10 }}>{loadError}</div>}
          </Card>

          {food && (
            <>
              <Card title="2. How much?" right={<Segmented<Mode> options={[{ value: 'batch', label: 'I know the raw amount' }, { value: 'portion', label: 'I only know the cooked portion' }]} value={mode} onChange={setMode} />}>
                <div className="stack" style={{ gap: 12 }}>
                  <div className="amount-row">
                    <input className="input" type="number" min={0} step="any" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} aria-label="Amount" />
                    <select className="input" value={unit.id} onChange={(e) => setUnitId(e.target.value)} aria-label="Unit">
                      {allUnits.map((u) => <option key={u.id} value={u.id}>{u.id.startsWith('p') ? `${u.label} (${u.grams} g)` : u.label}</option>)}
                    </select>
                  </div>
                  <p className="faint" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                    {mode === 'batch' ? `= ${fmt(amountGrams)} g raw` : `= ${fmt(amountGrams)} g cooked, on your plate`}
                  </p>
                  <label className="field"><span>How did you get that amount?</span>
                    <select className="input" value={amountMethod} onChange={(e) => setAmountMethod(e.target.value as AmountMethod)}>
                      {AMOUNT_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label} (±{Math.round(m.relError * 100)}%)</option>)}
                    </select>
                    <small>{AMOUNT_METHODS.find((m) => m.id === amountMethod)?.basis}</small>
                  </label>
                </div>
              </Card>

              <Card title="3. How was it cooked?">
                <div className="form-grid" style={{ gridTemplateColumns: usesFatHandling(kind) ? '1fr 1fr' : '1fr' }}>
                  <label className="field"><span>Cooking method</span>
                    <select className="input" value={method} onChange={(e) => { const m = e.target.value as CookMethod | ''; setMethod(m); if (m) setHandling(defaultFatHandling(m)); }}>
                      {methods.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
                      <option value="">Something else (I’ll weigh it)</option>
                    </select>
                  </label>
                  {usesFatHandling(kind) && (
                    <label className="field"><span>What happened to the fat?</span>
                      <select className="input" value={handling} onChange={(e) => setHandling(e.target.value as FatHandling)}>
                        {(Object.keys(FAT_HANDLING_LABEL) as FatHandling[]).map((h) => <option key={h} value={h}>{FAT_HANDLING_LABEL[h]}</option>)}
                      </select>
                    </label>
                  )}
                </div>
                {mode === 'batch' && (
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginTop: 14 }}>
                    <label className="field"><span>Cooked with oil or butter?</span>
                      <select className="input" value={fatId} onChange={(e) => setFatId(e.target.value as CookingFatId | '')}>
                        <option value="">No added fat</option>
                        {(Object.keys(COOKING_FATS) as CookingFatId[]).map((f) => <option key={f} value={f}>{COOKING_FATS[f].name}</option>)}
                      </select>
                    </label>
                    {fatId !== '' && (
                      <>
                        <label className="field"><span>Amount used</span>
                          <div className="amount-row" style={{ gridTemplateColumns: '1fr 80px' }}>
                            <input className="input" type="number" min={0} step="any" inputMode="decimal" value={fatUsed} onChange={(e) => setFatUsed(e.target.value)} placeholder="e.g. 1" />
                            <select className="input" value={fatUnit} onChange={(e) => setFatUnit(e.target.value as FatUnit)}><option value="tbsp">tbsp</option><option value="tsp">tsp</option><option value="g">g</option></select>
                          </div>
                        </label>
                        <label className="field"><span>Left in the pan (optional)</span>
                          <input className="input" type="number" min={0} step="any" inputMode="decimal" value={fatLeft} onChange={(e) => setFatLeft(e.target.value)} placeholder="same unit" />
                          <small>Only what ends up in the food counts.</small>
                        </label>
                      </>
                    )}
                  </div>
                )}
                {mode === 'batch' && method === 'deep_fried' && (
                  <div className="note info" style={{ marginTop: 10 }}>Deep-fried food soaks up oil: weigh the oil (or the pot) before and after, and enter the difference above. For scale, French fries end up with about 13 g of oil per 100 g (Ziaiifar 2008).</div>
                )}
                {mode === 'batch' && (
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 14 }}>
                    <label className="field"><span>Cooked batch weight (optional)</span>
                      <div className="amount-row" style={{ gridTemplateColumns: '1fr 80px' }}>
                        <input className="input" type="number" min={0} step="any" inputMode="decimal" value={cookedQty} placeholder="skip if you didn't weigh" onChange={(e) => setCookedQty(e.target.value)} />
                        <select className="input" value={cookedUnit} onChange={(e) => setCookedUnit(e.target.value)}>{MASS_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}</select>
                      </div>
                      <small>Most accurate. Without it we use USDA’s measured yield for this food and method.</small>
                    </label>
                    <label className="field"><span>Split into pieces? (optional)</span>
                      <input className="input" type="number" min={0} step={1} value={pieces} placeholder="e.g. 4 patties" onChange={(e) => setPieces(e.target.value)} />
                      <small>Lets you log “1 patty” later without weighing.</small>
                    </label>
                  </div>
                )}
              </Card>
            </>
          )}
        </div>

        <div className="stack">
          {!food && <Card><Empty title="Pick a raw food to start">Ground beef, chicken breast, salmon, rice, pasta, lentils… USDA has measured cooking yields for 170+ meats, and we derived yields for common fish, grains and beans.</Empty></Card>}
          {food && result && 'error' in result && <Card><div className="note warn">{result.error}</div></Card>}
          {r && (
            <>
              <Card title={mode === 'batch' ? 'Your cooked batch' : 'Your portion'}>
                <div className="macro-strip">
                  <div><span>Raw</span><b className="num">{fmt(r.rawGrams)}</b><small className="faint">g</small></div>
                  <div><span>Cooked</span><b className="num">{fmt(r.cookedGrams)}</b><small className="faint">g ({fmt(r.yield.yield * 100)}% of raw)</small></div>
                  <div><span>{mode === 'batch' ? 'Whole batch' : 'Your portion'}</span><b className="num">{fmt(r.total.energy_kcal ?? 0)}</b><small className="faint">kcal ±{fmt(uncertainty * 100)}%</small></div>
                  <div><span>Per 100 g cooked</span><b className="num">{fmt(r.per100Cooked.energy_kcal ?? 0)}</b><small className="faint">kcal (raw: {fmt(eff?.values.energy_kcal ?? 0)})</small></div>
                </div>
                <div className="grid cols-3" style={{ marginTop: 12, gap: 10 }}>
                  <div className="tile"><div className="label">Per 100 g cooked</div><div className="value num" style={{ fontSize: 22 }}>{fmt(r.per100Cooked.energy_kcal ?? 0)}<small>kcal</small></div><div className="delta muted num">{fmt(r.per100Cooked.protein_g ?? 0, 1)} g protein · {fmt(r.per100Cooked.carbs_g ?? 0, 1)} g carbs · {fmt(r.per100Cooked.fat_g ?? 0, 1)} g fat</div></div>
                  {nPieces > 1 && mode === 'batch' && <div className="tile"><div className="label">Per piece (1 of {nPieces})</div><div className="value num" style={{ fontSize: 22 }}>{fmt((r.total.energy_kcal ?? 0) / nPieces)}<small>kcal</small></div><div className="delta muted num">{fmt(r.cookedGrams / nPieces)} g · {fmt((r.total.protein_g ?? 0) / nPieces, 1)} g protein</div></div>}
                  <div className="tile"><div className="label">Fat that dripped away</div><div className="value num" style={{ fontSize: 22 }}>{fmt(r.fatRemovedG, 1)}<small>g</small></div>
                    <div className={`delta ${r.capped ? 'warn' : 'muted'}`}>−{fmt(r.kcalRemoved)} kcal ({fmt(r.kcalRemovedShare * 100)}%){r.capped ? ' · capped at 20%' : ''}</div></div>
                  {r.fatAddedG > 0 && <div className="tile"><div className="label">Oil or butter added</div><div className="value num" style={{ fontSize: 22 }}>{fmt(r.fatAddedG, 1)}<small>g</small></div><div className="delta muted">+{fmt(r.kcalAdded)} kcal</div></div>}
                </div>
                <ul className="cite" style={{ margin: '12px 0 0', paddingLeft: 18 }}>
                  {r.notes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              </Card>

              {mode === 'batch' && (
                <Card title="Save & log">
                  <div className="stack" style={{ gap: 12 }}>
                    <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
                      <label className="field" style={{ flex: 1, minWidth: 200 }}><span>Name for this batch</span><input className="input" value={name} maxLength={300} onChange={(e) => setName(e.target.value)} /></label>
                      <button type="button" className="btn btn-primary" onClick={() => void saveBatch()} disabled={saving || savedFood !== null}>{saving ? <Spinner /> : <Icon name={savedFood ? 'check' : 'plus'} size={16} />} {savedFood ? 'Saved to My foods' : 'Save batch to My foods'}</button>
                    </div>
                    <p className="faint" style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>Saved batches show up in search, so you can log leftovers tomorrow by piece, fraction or cooked grams.</p>
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                      <strong style={{ fontSize: 14 }}>How much did you eat?</strong>
                      <div className="row" style={{ marginTop: 8, gap: 8 }}>
                        <Segmented<EatBy> options={[{ value: 'fraction', label: 'Part of batch' }, ...(nPieces > 1 ? [{ value: 'pieces' as EatBy, label: 'Pieces' }] : []), { value: 'grams', label: 'Cooked grams' }]} value={eatBy} onChange={setEatBy} />
                      </div>
                      <div className="row" style={{ marginTop: 10, gap: 8 }}>
                        {eatBy === 'fraction' && FRACTIONS.map((f) => (
                          <button key={f.label} type="button" className={`btn btn-sm ${Math.abs(eatFraction - f.value) < 1e-9 ? 'btn-primary' : ''}`} onClick={() => setEatFraction(f.value)}>{f.label}</button>
                        ))}
                        {eatBy !== 'fraction' && <input className="input" style={{ width: 120 }} type="number" min={0} step="any" value={eatQty} onChange={(e) => setEatQty(e.target.value)} aria-label="Amount eaten" />}
                        {eatBy === 'pieces' && <span className="muted">of {nPieces}</span>}
                        {eatBy === 'grams' && <select className="input" style={{ width: 'auto' }} value={eatMethod} onChange={(e) => setEatMethod(e.target.value as AmountMethod)}>{AMOUNT_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>}
                      </div>
                      <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
                        <span className="num" style={{ fontWeight: 700 }}>{fmt(eatenGrams)} g cooked · {fmt(((r.per100Cooked.energy_kcal ?? 0) * eatenGrams) / 100)} kcal (±{fmt(eatenUncertainty * 100)}%) · {fmt(((r.per100Cooked.protein_g ?? 0) * eatenGrams) / 100, 1)} g protein · {fmt(((r.per100Cooked.carbs_g ?? 0) * eatenGrams) / 100, 1)} g carbs · {fmt(((r.per100Cooked.fat_g ?? 0) * eatenGrams) / 100, 1)} g fat</span>
                        <button type="button" className="btn btn-primary" onClick={logPortion} disabled={eatenGrams <= 0}><Icon name="plus" size={16} /> Add to diary</button>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
              {mode === 'portion' && (
                <Card title="Log it">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="muted" style={{ fontWeight: 600 }}>That portion came from about <b>{fmt(r.rawGrams)} g</b> of raw food.</span>
                    <button type="button" className="btn btn-primary" onClick={() => { setEatBy('grams'); setEatQty(String(Math.round(r.cookedGrams))); const it = itemFromFood({ ...food!, name: name.trim() || `${shortName(food!.name)}, cooked` }, r.per100Cooked, r.cookedGrams, Math.round(r.cookedGrams), 'g'); delete it.food_id; setLogging(it); }}><Icon name="plus" size={16} /> Add to diary</button>
                  </div>
                </Card>
              )}

              <Card title="All nutrients" sub="per 100 g cooked">
                <NutrientPanel values={r.per100Cooked} showSources={false} defaultOpen={['general', 'carbohydrates', 'lipids']} />
              </Card>
            </>
          )}
        </div>
      </div>
      {logging && <AddToDiaryModal item={logging} onClose={() => setLogging(null)} />}
    </div>
  );
}
